import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import Sheet from '../components/Sheet';
import Avatar from '../components/Avatar';
import { PRIMARY } from '../components/CaptureSheets';
import { WEEKDAY_SHORT, today } from '../lib/dates';
import { DURATIONS, FOCUS, NOTE_MAX, atTime, clock, deviceZone, joinNote, localDate, splitNote, weekdayShort } from '../lib/schedule';
import Icon from '../components/Icon';
import { useApp, useAppPath } from '../lib/AppContext';
import { LimitSheet, OfflineForm, OfflineTag, StudentPicker, useOfflineClients } from './OfflineSheets';

const LONG_WEEKDAY = { T2: 'Thứ Hai', T3: 'Thứ Ba', T4: 'Thứ Tư', T5: 'Thứ Năm', T6: 'Thứ Sáu', T7: 'Thứ Bảy', CN: 'Chủ nhật' };
const CHIP = 'h-11 rounded-[12px] border px-3 text-sm font-semibold';
const chip = (on) => `${CHIP} ${on ? 'border-[#17804f] bg-[#e3f1e9] text-[#17804f]' : 'border-line bg-base-100'}`;
const LABEL = 'text-xs font-bold uppercase tracking-wide text-muted';

const blank = (who, date) => ({ who: who || null, date: date || today(), time: '18:00', duration: 60, custom: '', location: null, focus: [], extra: '', repeat: false });

// Who an appointment is for: a connected student, or someone without the app (Người chưa dùng app).
const studentWho = (s) => s && { kind: 'student', id: s.client_id, name: s.name, avatar_url: s.avatar_url };

// A4 "Thêm lịch tập" (or editing one): who (F1 picker: students and people without the app, where one can be
// added, F2), day, time, length, where, and weekly repeat. Saving tells a student; someone without the app is
// told nothing. Editing an occurrence of a weekly series asks whether it is only this one or the ones after too.
export default function AppointmentSheet({ open, students, editing, defaults, lastLocation, onClose, onSaved }) {
  const { api } = useApp();
  const navigate = useNavigate();
  const path = useAppPath();
  const [form, setForm] = useState(blank());
  const [scope, setScope] = useState('one');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [offline, reloadOffline] = useOfflineClients(open && !editing);
  const [step, setStep] = useState(null); // null | 'pick' | 'add' | 'limit'
  const [limitShown, setLimitShown] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setScope('one');
    setStep(null);
    if (editing) {
      setForm({ who: editing.offline ? { kind: 'offline', id: editing.offline.id, name: editing.offline.name } : studentWho({ client_id: editing.client_id, name: editing.client_name, avatar_url: editing.client_avatar_url }),
        date: localDate(editing.starts_at), time: clock(editing.starts_at),
        duration: DURATIONS.includes(editing.duration_min) ? editing.duration_min : 'custom', custom: String(editing.duration_min),
        location: editing.location || '', ...splitNote(editing.note), repeat: editing.repeats });
    } else {
      const student = students.find((s) => String(s.client_id) === String(defaults?.clientId));
      setForm({ ...blank(student ? studentWho(student) : null, defaults?.date), location: lastLocation || '' });
    }
  }, [open, editing, defaults, students, lastLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Thêm lịch tập" from an offline client's options (?add_offline=<id>) once their list is in.
  useEffect(() => {
    if (!open || editing || !defaults?.offlineId || !offline) return;
    const client = offline.offline_clients.find((c) => String(c.id) === String(defaults.offlineId));
    if (client) setForm((f) => ({ ...f, who: { kind: 'offline', id: client.id, name: client.name } }));
  }, [open, editing, defaults, offline]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const who = form.who;
  const isOffline = who?.kind === 'offline';
  const minutes = form.duration === 'custom' ? Number(form.custom) : form.duration;
  const weekday = useMemo(() => weekdayShort(form.date), [form.date]);
  const note = joinNote(form.focus, form.extra);
  const valid = who && form.date && form.time && minutes >= 15 && minutes <= 240 && (note || '').length <= NOTE_MAX;
  const toggleFocus = (f) => setForm((s) => ({ ...s, focus: s.focus.includes(f) ? s.focus.filter((x) => x !== f) : [...s.focus, f] }));
  // Only the note changed: the student isn't notified, they see it in the app.
  const quiet = editing && form.date === localDate(editing.starts_at) && form.time === clock(editing.starts_at)
    && minutes === editing.duration_min && (form.location?.trim() || null) === (editing.location || null);
  const limit = offline?.limit;
  const startAdd = () => (limit && limit.count >= limit.limit ? (setLimitShown(limit), setStep('limit')) : setStep('add'));

  const save = async (e) => {
    e.preventDefault();
    setBusy('save');
    setError(null);
    try {
      const body = { starts_at: atTime(form.date, form.time), duration_min: minutes, location: form.location?.trim() || null, note };
      const target = isOffline ? { offline_client_id: who.id } : { client_id: who.id };
      const saved = editing
        ? await api.patch(`/coach/appointments/${editing.id}`, { ...body, scope })
        : await api.post('/coach/appointments', { ...body, ...target, repeat_weekly: form.repeat, time_zone: deviceZone() });
      if (isOffline) onSaved(saved, editing ? 'Đã lưu' : `Đã hẹn ${who.name}`);
      else onSaved(saved, quiet ? 'Đã lưu' : editing ? 'Đã lưu và báo cho học viên' : `Đã hẹn và báo cho ${who.name}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };
  const cancel = async () => {
    const which = editing.repeats ? scope : 'one';
    const told = isOffline ? '' : ' Học viên sẽ được báo.';
    if (!window.confirm(which === 'following' ? `Huỷ buổi này và các buổi sau?${told}` : `Huỷ buổi này?${told}`)) return;
    setBusy('cancel');
    setError(null);
    try {
      await api.del(`/coach/appointments/${editing.id}?scope=${which}`);
      onSaved(null, isOffline ? 'Đã huỷ' : 'Đã huỷ và báo cho học viên');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title={editing ? 'Sửa lịch tập' : 'Thêm lịch tập'}>
      <form className="flex flex-col gap-4" onSubmit={save}>
        <div className="flex flex-col gap-1.5">
          <span id="appt-student" className={LABEL}>Học viên</span>
          {editing ? (
            <p className="flex h-12 items-center gap-2 rounded-[14px] border border-line px-3 font-semibold">
              <Avatar name={who?.name} url={who?.avatar_url} dashed={isOffline} size={28} /> {who?.name} {isOffline && <OfflineTag />}
            </p>
          ) : (
            <button type="button" aria-labelledby="appt-student" onClick={() => setStep('pick')}
              className="flex h-12 items-center gap-2 rounded-[14px] border border-line px-3 text-left font-semibold">
              {who ? <><Avatar name={who.name} url={who.avatar_url} dashed={isOffline} size={28} /> <span className="flex-1 truncate">{who.name}</span>{isOffline && <OfflineTag />}</>
                : <span className="flex-1 font-normal text-muted">Chọn học viên</span>}
              <Icon name="chevronRight" className="size-4 text-muted" />
            </button>
          )}
          {isOffline && !editing && <p className="text-xs text-muted">{who.name} chưa dùng app nên không nhận thông báo.</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="appt-date" className={LABEL}>Ngày</label>
            <input id="appt-date" type="date" className="input h-12 w-full rounded-[14px] border-line text-base" min={today()}
              value={form.date} onChange={(e) => set('date')(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="appt-time" className={LABEL}>Giờ</label>
            <input id="appt-time" type="time" step={300} className="input h-12 w-full rounded-[14px] border-line text-base"
              value={form.time} onChange={(e) => set('time')(e.target.value)} />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className={LABEL}>Thời lượng</span>
          <div className="flex flex-wrap gap-2">
            {DURATIONS.map((d) => <button key={d} type="button" className={chip(form.duration === d)} onClick={() => set('duration')(d)}>{d}′</button>)}
            <button type="button" className={chip(form.duration === 'custom')} onClick={() => set('duration')('custom')}>Khác</button>
            {form.duration === 'custom' && (
              <label className="flex items-center gap-1.5 text-sm">
                <input type="number" inputMode="numeric" min={15} max={240} aria-label="Số phút" className="input h-11 w-20 rounded-[12px] border-line text-base"
                  value={form.custom} onChange={(e) => set('custom')(e.target.value)} /> phút
              </label>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="appt-place" className={`${LABEL} flex justify-between`}>
            Địa điểm {!editing && lastLocation && form.location === lastLocation && <span className="font-medium normal-case tracking-normal">dùng lần trước</span>}
          </label>
          <input id="appt-place" className="input h-12 w-full rounded-[14px] border-line text-base" maxLength={120} placeholder="VD: Gym Tân Bình"
            value={form.location || ''} onChange={(e) => set('location')(e.target.value)} />
        </div>

        <div className="flex flex-col gap-1.5">
          <span id="appt-focus" className={LABEL}>Nội dung buổi tập</span>
          <p className="text-xs text-muted">Chọn nhóm cơ của buổi này, ghi thêm bài chính hoặc điều học viên cần chuẩn bị. Học viên thấy trên lịch của họ.</p>
          <div role="group" aria-labelledby="appt-focus" className="flex flex-wrap gap-2">
            {FOCUS.map((f) => (
              <button key={f} type="button" aria-pressed={form.focus.includes(f)} className={chip(form.focus.includes(f))} onClick={() => toggleFocus(f)}>{f}</button>
            ))}
          </div>
          <input aria-label="Ghi chú thêm" className="input h-12 w-full rounded-[14px] border-line text-base" maxLength={NOTE_MAX} placeholder="VD: squat 4 hiệp, mang giày tập, ăn nhẹ trước 1 tiếng"
            value={form.extra} onChange={(e) => set('extra')(e.target.value)} />
          {(note || '').length > NOTE_MAX && <p className="text-xs text-error">Nội dung dài quá {NOTE_MAX} ký tự</p>}
        </div>

        {!editing && (
          <label className="flex min-h-14 items-center justify-between gap-3">
            <span>
              <span className="block text-[15px] font-semibold">Lặp lại hằng tuần</span>
              <span className="block text-xs text-muted">Mỗi {LONG_WEEKDAY[weekday] || WEEKDAY_SHORT[0]} {form.time}</span>
            </span>
            <input type="checkbox" className="toggle toggle-primary" checked={form.repeat} onChange={(e) => set('repeat')(e.target.checked)} />
          </label>
        )}

        {editing?.repeats && (
          <div role="radiogroup" aria-label="Áp dụng cho" className="grid grid-cols-2 gap-2">
            <button type="button" role="radio" aria-checked={scope === 'one'} className={chip(scope === 'one')} onClick={() => setScope('one')}>Chỉ buổi này</button>
            <button type="button" role="radio" aria-checked={scope === 'following'} className={chip(scope === 'following')} onClick={() => setScope('following')}>
              Buổi này và các buổi sau
            </button>
          </div>
        )}

        {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
        <button type="submit" className={PRIMARY} disabled={!valid || !!busy}>
          {busy === 'save' && <span className="loading loading-spinner loading-sm" />}
          {isOffline ? `Lưu lịch hẹn ${editing ? '' : who.name}`.trim() : quiet ? 'Lưu' : editing ? 'Lưu và báo cho học viên' : `Lưu và báo cho ${who?.name || 'học viên'}`}
        </button>
        {editing && (
          <button type="button" className="mx-auto min-h-11 px-4 text-sm font-semibold text-error" disabled={!!busy} onClick={cancel}>
            {editing.repeats && scope === 'following' ? 'Huỷ buổi này và các buổi sau' : 'Huỷ buổi này'}
          </button>
        )}
      </form>
      <StudentPicker open={step === 'pick'} students={students} offline={offline?.offline_clients} limit={limit} onClose={() => setStep(null)}
        onPick={(picked) => { set('who')(picked); setStep(null); }} onAdd={startAdd} />
      <OfflineForm open={step === 'add'} limit={limit} onClose={() => setStep('pick')}
        onSaved={(created) => { set('who')({ kind: 'offline', id: created.id, name: created.name }); setStep(null); reloadOffline(); }}
        onLimit={(l) => { setLimitShown(l || limit); setStep('limit'); reloadOffline(); }} />
      <LimitSheet open={step === 'limit'} limit={limitShown} onClose={() => setStep(null)}
        onFree={() => { setStep(null); onClose(); navigate(path('/coach?offline=1')); }} />
    </Sheet>
  );
}
